import axios from 'axios';
import Cookies from 'js-cookie';

// export const API_URL = 'http://127.0.0.1:8000';
export const API_URL = 'https://api.app-fotog.ru';

const TOKEN_KEY = 'photo_auth_token';


export const parseApiError = (err, fallback = 'Произошла ошибка') => {
    if (!err) return fallback;
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) return err.flat().filter(Boolean).join('; ') || fallback;
    if (typeof err === 'object') {
        // Спец-обработка ответа `permission_required` декоратора:
        // { error: "Недостаточно прав", required_permissions: [...] }
        if (err.required_permissions && err.error) {
            return `${err.error}: ${err.required_permissions.join(', ')}`;
        }
        if (err.detail) return parseApiError(err.detail, fallback);
        if (err.error)  return parseApiError(err.error, fallback);
        if (err.message) return parseApiError(err.message, fallback);
        // Объект формы { field: ['msg', ...], ... } — берём только строки и массивы строк
        const msgs = Object.values(err).flatMap(v =>
            typeof v === 'string' ? [v] :
            Array.isArray(v) ? v.filter(x => typeof x === 'string') : []
        );
        if (msgs.length) return msgs.join('; ');
    }
    return fallback;
};



// ─── Хелпер: заголовок авторизации ──────────────────────────────────────────
export const getAuthConfig = (token) => ({
    headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
    },
});

// ─── Хелпер: токен из куков ──────────────────────────────────────────────────
export const getToken = () => Cookies.get(TOKEN_KEY);

// ─── Хелпер: обработка ошибок ────────────────────────────────────────────────
const handleError = (error, fallback = null) => {
    if (error?.response) {
        console.error(`API ${error.response.status}:`, error.response.data);
    } else if (error?.code === 'ERR_NETWORK') {
        console.error('Ошибка сети — сервер недоступен');
    } else {
        console.error('API error:', error?.message);
    }
    return fallback;
};

// ════════════════════════════════════════════════════════════════════
//  Axios interceptor — глобальный перехват 401
// ════════════════════════════════════════════════════════════════════

let _onUnauthorized = null;

export const registerUnauthorizedHandler = (cb) => {
    _onUnauthorized = cb;
};

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        if (status === 401) {
            console.warn('API 401 — сессия истекла, выполняется выход…');
            Cookies.remove(TOKEN_KEY);
            if (typeof _onUnauthorized === 'function') {
                _onUnauthorized();
            } else {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    },
);


// ════════════════════════════════════════════════════════════════════
//  АВТОРИЗАЦИЯ
// ════════════════════════════════════════════════════════════════════

// POST /api/auth/login/
export const APILogin = async (email, password) => {
    try {
        const response = await axios.post(`${API_URL}/api/auth/login/`, { email, password });
        return response.data;   // { token, account }
    } catch (error) {
        return { error: error?.response?.data?.error || 'Ошибка входа' };
    }
};

// POST /api/auth/logout/
export const APILogout = async (token) => {
    try {
        await axios.post(`${API_URL}/api/auth/logout/`, {}, getAuthConfig(token));
        return true;
    } catch (error) {
        return handleError(error, false);
    }
};

// GET /api/auth/me/
export const APIGetMe = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/auth/me/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};


// ════════════════════════════════════════════════════════════════════
//  СОТРУДНИКИ
// ════════════════════════════════════════════════════════════════════

// GET /api/employees/?role=photographer|manager
export const APIGetEmployees = async (token, role = null, params = {}) => {
    try {
        // role может быть:
        //   - строкой → подставляем как ?role=...
        //   - объектом → мерджим в params
        //   - null    → ничего не добавляем
        let finalParams = { ...params };
        if (typeof role === 'string' && role) {
            finalParams.role = role;
        } else if (role && typeof role === 'object') {
            finalParams = { ...finalParams, ...role };
        }
        const response = await axios.get(`${API_URL}/api/employees/`, {
            ...getAuthConfig(token),
            params: finalParams,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// ── Роли ─────────────────────────────────────────────────────

// GET /api/roles/
export const APIGetRoles = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/roles/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/roles/<id>/
export const APIGetRoleDetail = async (token, roleId) => {
    try {
        const response = await axios.get(`${API_URL}/api/roles/${roleId}/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// POST /api/roles/create/
export const APICreateRole = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/roles/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания роли' };
    }
};

// PATCH /api/roles/<id>/update/
export const APIUpdateRole = async (token, roleId, data) => {
    try {
        const response = await axios.patch(`${API_URL}/api/roles/${roleId}/update/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка обновления роли' };
    }
};

// DELETE /api/roles/<id>/delete/
export const APIDeleteRole = async (token, roleId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/roles/${roleId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления роли' };
    }
};

// GET /api/permissions/?category=
export const APIGetPermissions = async (token, category = null) => {
    try {
        const params = category ? { category } : {};
        const response = await axios.get(`${API_URL}/api/permissions/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/employees/<id>/
export const APIGetEmployee = async (token, accountId) => {
    try {
        const response = await axios.get(`${API_URL}/api/employees/${accountId}/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// POST /api/employees/create/
export const APICreateEmployee = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/employees/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания сотрудника' };
    }
};

// PATCH /api/employees/<id>/update/
export const APIUpdateEmployee = async (token, accountId, data) => {
    try {
        const response = await axios.patch(`${API_URL}/api/employees/${accountId}/update/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка обновления сотрудника' };
    }
};

// DELETE /api/employees/<id>/delete/
export const APIDeleteEmployee = async (token, accountId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/employees/${accountId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления сотрудника' };
    }
};

// POST /api/employees/<id>/trash/?confirm=1
export const APITrashEmployee = async (token, accountId, reason = '') => {
    try {
        const response = await axios.post(
            `${API_URL}/api/employees/${accountId}/trash/?confirm=1`,
            { reason },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка отправки в корзину' };
    }
};

// POST /api/employees/<id>/restore-from-trash/
export const APIRestoreFromTrash = async (token, accountId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/employees/${accountId}/restore-from-trash/`,
            {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка восстановления' };
    }
};

// GET /api/employees/trashed/
export const APIGetTrashedEmployees = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/employees/trashed/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return handleError(error, []); }
};

// DELETE /api/employees/<id>/delete-permanent/?confirm=1&confirm2=1
export const APIDeleteEmployeePermanent = async (token, accountId) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/employees/${accountId}/delete-permanent/?confirm=1&confirm2=1`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка окончательного удаления' };
    }
};

// POST /api/employees/<id>/reset-password/
export const APIResetEmployeePassword = async (token, accountId, newPassword) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/employees/${accountId}/reset-password/`,
            { new_password: newPassword },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка смены пароля' };
    }
};


// ════════════════════════════════════════════════════════════════════
//  ТОЧКИ (ЛОКАЦИИ)
// ════════════════════════════════════════════════════════════════════

// GET /api/locations/
export const APIGetLocations = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/locations/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/locations/create/
export const APICreateLocation = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/locations/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания точки' };
    }
};

// PATCH /api/locations/<id>/update/
export const APIUpdateLocation = async (token, locationId, data) => {
    try {
        const response = await axios.patch(`${API_URL}/api/locations/${locationId}/update/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка обновления точки' };
    }
};

// DELETE /api/locations/<id>/delete/
export const APIDeleteLocation = async (token, locationId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/locations/${locationId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления точки' };
    }
};


// ════════════════════════════════════════════════════════════════════
//  ДОСТУПНОСТЬ (ГРАФИК ФОТОГРАФА)
// ════════════════════════════════════════════════════════════════════

// GET /api/availability/my/
export const APIGetMyAvailability = async (token, weekStart = null) => {
    try {
        const params = weekStart ? { week_start: weekStart } : {};
        const response = await axios.get(`${API_URL}/api/availability/my/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/availability/my/set/
// data: { days: [{ date, preferred_time }, ...] }
export const APISetMyAvailability = async (token, days) => {
    try {
        const response = await axios.post(`${API_URL}/api/availability/my/set/`, { days }, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка сохранения графика' };
    }
};

// GET /api/availability/all/  (руководитель — кто заполнил)
export const APIGetAllAvailability = async (token, weekStart = null) => {
    try {
        const params = weekStart ? { week_start: weekStart } : {};
        const response = await axios.get(`${API_URL}/api/availability/all/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/week-summary/?week_start=2026-06-15
export const APIGetWeekSummary = async (token, weekStart) => {
    try {
        const response = await axios.get(`${API_URL}/api/week-summary/`, {
            ...getAuthConfig(token),
            params: { week_start: weekStart },
        });
        return response.data;
    } catch (error) { return handleError(error, null); }
};

// GET /api/availability/for-date/?date=YYYY-MM-DD  (руководитель — кто доступен в этот день)
export const APIGetAvailableForDate = async (token, date) => {
    try {
        const response = await axios.get(`${API_URL}/api/availability/for-date/`, {
            ...getAuthConfig(token),
            params: { date },
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};


// ════════════════════════════════════════════════════════════════════
//  НАЗНАЧЕНИЯ
// ════════════════════════════════════════════════════════════════════

// GET /api/assignments/my/
export const APIGetMyAssignments = async (token, weekStart = null) => {
    try {
        const params = weekStart ? { week_start: weekStart } : {};
        const response = await axios.get(`${API_URL}/api/assignments/my/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/assignments/colleagues/?date=YYYY-MM-DD
export const APIGetColleaguesAssignments = async (token, date) => {
    try {
        const response = await axios.get(`${API_URL}/api/assignments/colleagues/`, {
            ...getAuthConfig(token),
            params: { date },
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/assignments/day/?date=YYYY-MM-DD
export const APIGetDayAssignments = async (token, date) => {
    try {
        const response = await axios.get(`${API_URL}/api/assignments/day/`, {
            ...getAuthConfig(token),
            params: { date },
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/assignments/create/
// data: { date, assignments: [{ location_id, account_id, start_time, time_source, is_admin }, ...] }
export const APICreateAssignments = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/assignments/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания назначений' };
    }
};

// POST /api/assignments/confirm/
// data: { date }
export const APIConfirmDay = async (token, date) => {
    try {
        const response = await axios.post(`${API_URL}/api/assignments/confirm/`, { date }, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка подтверждения' };
    }
};

// DELETE /api/assignments/<id>/delete/
export const APIDeleteAssignment = async (token, assignmentId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/assignments/${assignmentId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления назначения' };
    }
};


// ════════════════════════════════════════════════════════════════════
//  ЗАПРОСЫ НА ИЗМЕНЕНИЕ ГРАФИКА
// ════════════════════════════════════════════════════════════════════

// POST /api/change-requests/create/
// data: { date, change_type, new_time?, comment? }
export const APICreateChangeRequest = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/change-requests/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания запроса' };
    }
};

// POST /api/change-requests/create-batch/
export const APICreateChangeRequestsBatch = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/change-requests/create-batch/`, data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка' };
    }
};

// GET /api/change-requests/my/
export const APIGetMyChangeRequests = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/change-requests/my/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/change-requests/pending/  (руководитель)
export const APIGetPendingChangeRequests = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/change-requests/pending/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/change-requests/<id>/review/
// data: { action: 'approve'|'reject', comment? }
export const APIReviewChangeRequest = async (token, requestId, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/change-requests/${requestId}/review/`,
            data,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка рассмотрения запроса' };
    }
};

// POST /api/change-requests/review-batch/  body: { ids:[...], action:'approve'|'reject', comment? }
export const APIReviewChangeRequestsBatch = async (token, ids, action, comment = '') => {
    try {
        const response = await axios.post(
            `${API_URL}/api/change-requests/review-batch/`,
            { ids, action, comment },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка массового рассмотрения' };
    }
};

// POST /api/fill-requests/review-batch/  body: { ids:[...], action:'approve'|'reject', comment? }
export const APIReviewFillRequestsBatch = async (token, ids, action, comment = '') => {
    try {
        const response = await axios.post(
            `${API_URL}/api/fill-requests/review-batch/`,
            { ids, action, comment },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка массового рассмотрения' };
    }
};


// ════════════════════════════════════════════════════════════════════
//  МОТИВАЦИОННЫЕ СООБЩЕНИЯ
// ════════════════════════════════════════════════════════════════════

// GET /api/messages/
export const APIGetMotivationalMessages = async (token, statusFilter = null) => {
    try {
        const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
        const response = await axios.get(`${API_URL}/api/messages/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/messages/create/
export const APICreateMotivationalMessage = async (token, data) => {
    try {
        // data может быть строкой (обратная совместимость) или объектом
        const body = typeof data === 'string' ? { text: data, send_type: 'scheduled', audience: 'photographers' } : data;
        const response = await axios.post(`${API_URL}/api/messages/create/`, body, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания сообщения' };
    }
};

// PATCH /api/messages/<id>/update/
export const APIUpdateMotivationalMessage = async (token, messageId, data) => {
    try {
        const response = await axios.patch(`${API_URL}/api/messages/${messageId}/update/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка обновления сообщения' };
    }
};

// DELETE /api/messages/<id>/delete/
export const APIDeleteMotivationalMessage = async (token, messageId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/messages/${messageId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления сообщения' };
    }
};

// POST /api/messages/count-audience/
export const APICountAudience = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/messages/count-audience/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, { count: 0 });
    }
};

// ════════════════════════════════════════════════════════════════════
//  ДАШБОРД
// ════════════════════════════════════════════════════════════════════

// GET /api/dashboard/
export const APIGetDashboard = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/dashboard/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};


// ════════════════════════════════════════════════════════════════════
//  PUSH УВЕДОМЛЕНИЯ
// ════════════════════════════════════════════════════════════════════

// POST /api/push/subscribe/
export const APIRegisterPushSubscription = async (token, subscriptionData) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/push/subscribe/`,
            subscriptionData,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// POST /api/push/unsubscribe/
export const APIUnregisterPushSubscription = async (token, endpoint) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/push/unsubscribe/`,
            { endpoint },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// GET /api/push/subscriptions/
export const APIGetPushSubscriptions = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/push/subscriptions/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/push/test/
export const APITestPushNotification = async (token) => {
    try {
        const response = await axios.post(`${API_URL}/api/push/test/`, {}, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// GET /api/push/check-token/?device_token=
export const APICheckPushByToken = async (token, deviceToken) => {
    try {
        const response = await axios.get(`${API_URL}/api/push/check-token/`, {
            ...getAuthConfig(token),
            params: { device_token: deviceToken },
        });
        return response.data;   // { exists: true/false }
    } catch (error) {
        return handleError(error, { exists: false });
    }
};


// ════════════════════════════════════════════════════════════════════
//  ЗАЯВКИ НА ЗАПОЛНЕНИЕ ГРАФИКА
// ════════════════════════════════════════════════════════════════════

// POST /api/fill-requests/create/
export const APICreateFillRequest = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/fill-requests/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания заявки' };
    }
};

// GET /api/fill-requests/my/
export const APIGetMyFillRequests = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/fill-requests/my/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/fill-requests/pending/ (руководитель)
export const APIGetPendingFillRequests = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/fill-requests/pending/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/fill-requests/all/ (руководитель)
export const APIGetAllFillRequests = async (token, statusFilter = null) => {
    try {
        const params = statusFilter ? { status: statusFilter } : {};
        const response = await axios.get(`${API_URL}/api/fill-requests/all/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/fill-requests/<id>/review/ (руководитель)
export const APIReviewFillRequest = async (token, requestId, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/fill-requests/${requestId}/review/`,
            data,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка рассмотрения заявки' };
    }
};

// GET /api/fill-requests/check-access/?week_start=YYYY-MM-DD
export const APICheckFillAccess = async (token, weekStart) => {
    try {
        const response = await axios.get(`${API_URL}/api/fill-requests/check-access/`, {
            ...getAuthConfig(token),
            params: { week_start: weekStart },
        });
        return response.data;
    } catch (error) {
        return handleError(error, { can_fill: false, deadline: null });
    }
};


// ════════════════════════════════════════════════════════════════════
//  УТИЛИТЫ PUSH
// ════════════════════════════════════════════════════════════════════

// Конвертация VAPID public key из base64url в Uint8Array
export const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};


// ════════════════════════════════════════════════════════════════════
//  ЗАЯВКИ НА ЗАМЕНУ
// ════════════════════════════════════════════════════════════════════

// POST /api/substitutions/create/
export const APICreateSubstitutionRequest = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/substitutions/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания заявки' };
    }
};

// GET /api/substitutions/my/
export const APIGetMySubstitutionRequests = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/substitutions/my/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/substitutions/all/?status=...
export const APIGetAllSubstitutionRequests = async (token, statusFilter = null) => {
    try {
        const params = statusFilter ? { status: statusFilter } : {};
        const response = await axios.get(`${API_URL}/api/substitutions/all/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/substitutions/pending/
export const APIGetPendingSubstitutionRequests = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/substitutions/pending/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/substitutions/available/
export const APIGetAvailableSubstitutions = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/substitutions/available/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/substitutions/<id>/review/
export const APIReviewSubstitutionRequest = async (token, requestId, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/substitutions/${requestId}/review/`, data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка' };
    }
};

// GET /api/substitutions/<id>/broadcast-candidates/?...
export const APIGetBroadcastCandidates = async (token, requestId, filters = {}) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/substitutions/${requestId}/broadcast-candidates/`,
            { ...getAuthConfig(token), params: filters },
        );
        return response.data;
    } catch (error) {
        return handleError(error, { candidates: [], total_count: 0 });
    }
};

// POST /api/substitutions/<id>/broadcast/
export const APIBroadcastSubstitution = async (token, requestId, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/substitutions/${requestId}/broadcast/`, data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка рассылки' };
    }
};

// POST /api/substitutions/<id>/accept/
export const APIAcceptSubstitution = async (token, requestId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/substitutions/${requestId}/accept/`, {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка' };
    }
};

// POST /api/substitutions/<id>/decline/
export const APIDeclineSubstitution = async (token, requestId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/substitutions/${requestId}/decline/`, {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка' };
    }
};

// DELETE /api/substitutions/<id>/cancel/
export const APICancelSubstitutionRequest = async (token, requestId) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/substitutions/${requestId}/cancel/`, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка' };
    }
};



// ════════════════════════════════════════════════════════════════════
//  ФИНАНСЫ (ЧЕКИ / ПРЕМИИ / ШТРАФЫ)
// ════════════════════════════════════════════════════════════════════

// GET /api/finance/orders/?date_from=&date_to=&photographer_id=&location_id=
export const APIGetOrders = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/orders/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/finance/orders/create/
export const APICreateOrder = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/finance/orders/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания чека' };
    }
};

// POST /api/finance/orders/self/  body: { value, money_type }
// Фотограф сам создаёт чек напрямую (только на точке без админов)
export const APICreateOrderSelf = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/orders/self/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Ошибка создания чека' }; }
};

// POST /api/finance/orders/batch/
export const APICreateOrdersBatch = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/finance/orders/batch/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания чеков' };
    }
};

// DELETE /api/finance/orders/<id>/delete/
export const APIDeleteOrder = async (token, orderId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/finance/orders/${orderId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления чека' };
    }
};



// ── Отчёты / Статистика ──────────────────────────────────────────

// GET /api/finance/reports/?date_from=&date_to=&location_id=
export const APIGetReport = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/reports/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// GET /api/finance/reports/today/
export const APIGetReportToday = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/reports/today/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// GET /api/finance/my-stats/?date_from=&date_to=
export const APIGetMyStats = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/my-stats/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// ── Премии (Reward) ──────────────────────────────────────────

// GET /api/finance/rewards/?date_from=&date_to=&photographer_id=
export const APIGetRewards = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/rewards/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/finance/rewards/create/
export const APICreateReward = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/finance/rewards/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания премии' };
    }
};

// DELETE /api/finance/rewards/<id>/delete/
export const APIDeleteReward = async (token, rewardId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/finance/rewards/${rewardId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления премии' };
    }
};


// ── Штрафы (Fine) ────────────────────────────────────────────

// GET /api/finance/fines/?date_from=&date_to=&photographer_id=&is_deduction=&is_active=
export const APIGetFines = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/fines/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/finance/fines/create/
export const APICreateFine = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/finance/fines/create/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания штрафа' };
    }
};

// PATCH /api/finance/fines/<id>/cancel/  (soft-delete, отмена)
export const APICancelFine = async (token, fineId) => {
    try {
        const response = await axios.patch(`${API_URL}/api/finance/fines/${fineId}/cancel/`, {}, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка отмены штрафа' };
    }
};

// DELETE /api/finance/fines/<id>/delete/
export const APIDeleteFine = async (token, fineId) => {
    try {
        const response = await axios.delete(`${API_URL}/api/finance/fines/${fineId}/delete/`, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления штрафа' };
    }
};


// ── Настройки / Очистка ──────────────────────────────────────

// POST /api/finance/clear-month/
export const APIClearMonth = async (token, data) => {
    try {
        const response = await axios.post(`${API_URL}/api/finance/clear-month/`, data, getAuthConfig(token));
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка очистки данных' };
    }
};



// ════════════════════════════════════════════════════════════════════
//  УВЕДОМЛЕНИЯ О ПРОДАЖАХ (SaleNotification)
// ════════════════════════════════════════════════════════════════════

// POST /api/finance/sale-notifications/create/
export const APICreateSaleNotification = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/sale-notifications/create/`, data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка отправки' };
    }
};

// GET /api/finance/sale-notifications/my/?date=
export const APIGetMySaleNotifications = async (token, date = null) => {
    try {
        const params = date ? { date } : {};
        const response = await axios.get(`${API_URL}/api/finance/sale-notifications/my/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/finance/sale-notifications/pending/?location_id=&date=
export const APIGetPendingSaleNotifications = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/sale-notifications/pending/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/finance/sale-notifications/<id>/process/
export const APIProcessSaleNotification = async (token, notificationId, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/sale-notifications/${notificationId}/process/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка оформления чека' };
    }
};

export const APICancelSaleNotification = async (token, notificationId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/sale-notifications/${notificationId}/cancel/`,
            {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка отмены' };
    }
};

// POST /api/finance/sale-notifications/<id>/restore/
// Возвращает отменённое уведомление обратно в pending
export const APIRestoreSaleNotification = async (token, notificationId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/sale-notifications/${notificationId}/restore/`,
            {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка восстановления' };
    }
};

// GET /api/finance/sale-notifications/overview/
// Сводка висящих (pending) чеков по точкам:
// { total_pending, locations: [{ location_id, location_name, pending_count,
//   oldest_pending_at, oldest_age_minutes, current_admin }] }
export const APIGetPendingSalesOverview = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/sale-notifications/overview/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, { total_pending: 0, locations: [] });
    }
};

// POST /api/schedule/manager-edit/
// Руководитель добавляет/убирает день в графике фотографа.
// data: { account_id, date, action: 'add'|'remove', preferred_time? }
export const APIManagerEditSchedule = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/schedule/manager-edit/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Не удалось изменить график' };
    }
};

// ════════════════════════════════════════════════════════════════════
//  WORKSTATION (рабочая станция)
// ════════════════════════════════════════════════════════════════════

// GET /api/workstation/admins/ — список админов, назначенных на сегодня
export const APIWorkstationGetAdmins = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/workstation/admins/`, getAuthConfig(token));
        return response.data;
    } catch (error) { return handleError(error, null); }
};

// POST /api/workstation/start-shift/  body: { admin_id }
export const APIWorkstationStartShift = async (token, adminId, photographerIds = []) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/workstation/start-shift/`,
            { admin_id: adminId, photographer_ids: photographerIds },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось начать смену' }; }
};

// POST /api/workstation/end-shift/
export const APIWorkstationEndShift = async (token, shiftId = null) => {
    try {
        const body = shiftId ? { shift_id: shiftId } : {};
        const response = await axios.post(
            `${API_URL}/api/workstation/end-shift/`, body, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось завершить смену' }; }
};

// GET /api/workstation/active-shift/  — { active, shifts: [{}, ...] }
// (новый формат: на одной workstation может быть несколько активных смен)
export const APIWorkstationActiveShift = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/workstation/active-shift/`, getAuthConfig(token));
        return response.data;
    } catch (error) { return handleError(error, { active: false, shifts: [] }); }
};

// GET /api/workstation/photographers-status/
// Список фотографов сегодня на этой точке (free / busy)
export const APIWorkstationPhotographersStatus = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/workstation/photographers-status/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return handleError(error, { photographers: [] }); }
};

export const APIWorkstationClaimPhotographer = async (token, shiftId, photographerId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/workstation/shift/${shiftId}/claim/`,
            { photographer_id: photographerId },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось привязать фотографа' }; }
};

// POST /api/workstation/reassign-photographer/  body: { photographer_id, location_id }
export const APIWorkstationReassignPhotographer = async (token, photographerId, locationId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/workstation/reassign-photographer/`,
            { photographer_id: photographerId, location_id: locationId },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось перенести фотографа' }; }
};

// POST /api/workstation/shift/<id>/release/  body: { photographer_id }
export const APIWorkstationReleasePhotographer = async (token, shiftId, photographerId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/workstation/shift/${shiftId}/release/`,
            { photographer_id: photographerId },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось отвязать фотографа' }; }
};

// GET /api/workstation/shift-history/  (для менеджера)
export const APIWorkstationShiftHistory = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/workstation/shift-history/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) { return handleError(error, { shifts: [] }); }
};

// GET /api/finance/workstation/stats/  — статистика для главного экрана workstation
export const APIWorkstationStats = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/workstation/stats/`, getAuthConfig(token));
        return response.data;
    } catch (error) { return handleError(error, null); }
};


// ════════════════════════════════════════════════════════════════════
//  ЖУРНАЛ СОБЫТИЙ (Audit / History)
// ════════════════════════════════════════════════════════════════════

// GET /api/events/?date_from=&date_to=&actor_id=&target_type=&level=&unseen_only=&page=
export const APIGetEvents = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/events/`, {
            ...getAuthConfig(token),
            params,
        });
        return response.data;   // { total, page, events: [...] }
    } catch (error) {
        return handleError(error, { total: 0, page: 1, events: [] });
    }
};

// POST /api/events/<id>/seen/
export const APIMarkEventSeen = async (token, eventId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/events/${eventId}/seen/`,
            {},
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// GET /api/events/unseen-count/
// Сервер теперь возвращает { total, critical, important, info }
export const APIGetEventsUnseenCount = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/events/unseen-count/`,
            getAuthConfig(token),
        );
        // Возвращаем как есть — { total, critical, important, info }
        // Старое поле `count` тоже подкладываем для обратной совместимости
        const data = response.data || {};
        return {
            ...data,
            count: data.total ?? data.count ?? 0,
        };
    } catch (error) {
        return handleError(error, { total: 0, critical: 0, important: 0, info: 0, count: 0 });
    }
};

// ══════════════════════════════════════════════════════════════════
//  ДОПОЛНИТЕЛЬНЫЕ ENDPOINTS (по новым серверным правкам)
// ══════════════════════════════════════════════════════════════════

// POST /api/events/mark-all-seen/
export const APIMarkAllEventsSeen = async (token) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/events/mark-all-seen/`,
            {},
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// GET /api/finance/my-rank/
export const APIGetMyRankToday = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/my-rank/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, { rank: 1, total: 1, today_total: 0, leader_total: 0 });
    }
};

// POST /api/roles/<id>/assign/
export const APIAssignRole = async (token, roleId, accountIds) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/roles/${roleId}/assign/`,
            { account_ids: accountIds },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка назначения роли' };
    }
};

// POST /api/roles/<id>/unassign/
export const APIUnassignRole = async (token, roleId, accountIds) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/roles/${roleId}/unassign/`,
            { account_ids: accountIds },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка снятия роли' };
    }
};

// POST /api/employees/<id>/fire/
export const APIFireEmployee = async (token, accountId, reason = '') => {
    try {
        const response = await axios.post(
            `${API_URL}/api/employees/${accountId}/fire/`,
            { reason },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка увольнения' };
    }
};

// POST /api/employees/<id>/restore/
export const APIRestoreEmployee = async (token, accountId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/employees/${accountId}/restore/`,
            {},
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка восстановления' };
    }
};

// POST /api/employees/<id>/avatar/   (multipart/form-data)
export const APIUploadAvatar = async (token, accountId, file) => {
    try {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(
            `${API_URL}/api/employees/${accountId}/avatar/`,
            fd,
            {
                headers: {
                    Authorization: `Token ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            },
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка загрузки аватарки' };
    }
};

// DELETE /api/employees/<id>/avatar/delete/
export const APIDeleteAvatar = async (token, accountId) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/employees/${accountId}/avatar/delete/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления аватарки' };
    }
};


// ══════════════════════════════════════════════════════════════════
//  КОНФИГУРАЦИЯ РАСЧЁТА (SalaryConfig)
// ══════════════════════════════════════════════════════════════════

// GET /api/finance/configs/
export const APIGetSalaryConfigs = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/configs/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// GET /api/finance/configs/<id>/
export const APIGetSalaryConfigDetail = async (token, configId) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/configs/${configId}/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, null);
    }
};

// POST /api/finance/configs/create/
export const APICreateSalaryConfig = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/configs/create/`,
            data,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка создания конфигурации' };
    }
};

// PATCH /api/finance/configs/<id>/update/
export const APIUpdateSalaryConfig = async (token, configId, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/api/finance/configs/${configId}/update/`,
            data,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка обновления конфигурации' };
    }
};

// DELETE /api/finance/configs/<id>/delete/
export const APIDeleteSalaryConfig = async (token, configId) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/finance/configs/${configId}/delete/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка удаления конфигурации' };
    }
};

// ══════════════════════════════════════════════════════════════════
//  МУЛЬТИ-ДОБАВЛЕНИЕ ЧЕКОВ (несколько фотографов одной операцией)
// ══════════════════════════════════════════════════════════════════

// POST /api/finance/orders/multi/
export const APICreateOrdersMulti = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/orders/multi/`,
            data,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка добавления чеков' };
    }
};


// ════════════════════════════════════════════════════════════════════
//  НОМЕРА ДЛЯ ПРИЁМА ОПЛАТЫ
// ════════════════════════════════════════════════════════════════════

// GET /api/finance/payment-numbers/?location_id=&is_active=
export const APIGetPaymentNumbers = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/payment-numbers/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) { return handleError(error, []); }
};

// POST /api/finance/payment-numbers/create/
export const APICreatePaymentNumber = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/payment-numbers/create/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось создать номер' }; }
};

// PATCH /api/finance/payment-numbers/<id>/update/
export const APIUpdatePaymentNumber = async (token, id, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/api/finance/payment-numbers/${id}/update/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось обновить номер' }; }
};

// DELETE /api/finance/payment-numbers/<id>/delete/
export const APIDeletePaymentNumber = async (token, id) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/finance/payment-numbers/${id}/delete/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось удалить номер' }; }
};

// GET /api/finance/payment-numbers/for-retro/
export const APIGetRetroNumbers = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/payment-numbers/for-retro/`, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, []);
    }
};

// POST /api/finance/payment-numbers/claim-retro/   body: { payment_number_id, reason }
export const APIClaimRetro = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/payment-numbers/claim-retro/`, data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Ошибка' };
    }
};

// ── Клайм-механика (для фотографа) ─────────────────────────────

// GET /api/finance/payment-numbers/my-active-claim/
export const APIGetMyActiveClaim = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/payment-numbers/my-active-claim/`,
            getAuthConfig(token),
        );
        return response.data;   // claim object или null
    } catch (error) { return handleError(error, null); }
};

// POST /api/finance/payment-numbers/claim/   body: { expected_value? }
export const APIClaimPaymentNumber = async (token, expectedValue = null) => {
    try {
        const body = expectedValue ? { expected_value: expectedValue } : {};
        const response = await axios.post(
            `${API_URL}/api/finance/payment-numbers/claim/`,
            body, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось получить номер' }; }
};

// POST /api/finance/payment-numbers/claims/<id>/confirm/
export const APIConfirmClaim = async (token, claimId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/payment-numbers/claims/${claimId}/confirm/`,
            {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось подтвердить' }; }
};

// POST /api/finance/payment-numbers/claims/<id>/cancel/
export const APICancelClaim = async (token, claimId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/payment-numbers/claims/${claimId}/cancel/`,
            {}, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось отменить' }; }
};

// GET /api/finance/payment-numbers/my-history/?date_from=&date_to=
export const APIGetMyClaimsHistory = async (token, params = {}) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/payment-numbers/my-history/`,
            { ...getAuthConfig(token), params },
        );
        return response.data;
    } catch (error) { return handleError(error, []); }
};

// GET /api/finance/payment-numbers/claims/?date_from=&date_to=&photographer_id=&status=
export const APIGetAllClaims = async (token, params = {}) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/finance/payment-numbers/claims/`,
            { ...getAuthConfig(token), params },
        );
        return response.data;
    } catch (error) { return handleError(error, []); }
};

// POST /api/me/notifications/toggle/
export const APIToggleMyNotifications = async (token, enabled) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/me/notifications/toggle/`,
            { enabled },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось переключить' }; }
};

// GET /api/me/notifications/silent-kinds/
// Возвращает { push_silent_kinds: [...], available_kinds: [...] }
export const APIGetMySilentKinds = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/api/me/notifications/silent-kinds/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return handleError(error, { push_silent_kinds: [], available_kinds: [] });
    }
};

// POST /api/me/notifications/silent-kinds/set/
// Body: { kinds: ['audit', 'assignment', ...] }
export const APISetMySilentKinds = async (token, kinds) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/me/notifications/silent-kinds/set/`,
            { kinds },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Не удалось обновить настройки звука' };
    }
};

// POST /api/me/notifications/muted-kinds/set/
// Body: { kinds: ['audit', 'sale_notification', ...] } — полностью отключить эти типы.
export const APISetMyMutedKinds = async (token, kinds) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/me/notifications/muted-kinds/set/`,
            { kinds },
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) {
        return { error: error?.response?.data || 'Не удалось обновить настройки уведомлений' };
    }
};

// ════════════════════════════════════════════════════════════════════
//  ТОВАРЫ
// ════════════════════════════════════════════════════════════════════

export const APIGetProducts = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/products/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) { return handleError(error, []); }
};

export const APICreateProduct = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/products/create/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось создать товар' }; }
};

export const APIUpdateProduct = async (token, id, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/api/finance/products/${id}/update/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось обновить' }; }
};

export const APIDeleteProduct = async (token, id) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/finance/products/${id}/delete/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось удалить' }; }
};

// GET /api/finance/stock/report/?date_from=&date_to=&location_id=
// → { date_from, date_to, items: [{ product_id, name, category_name,
//      stock (строка, дробное), is_unlimited (bool), sold_qty, sold_sum, written_off (строка) }] }
export const APIGetStockReport = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/stock/report/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) { return handleError(error, { items: [] }); }
};

// POST /api/finance/stock/replenish/  body: { comment?, items: [{ product_id, quantity }] }
export const APIReplenishStock = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/stock/replenish/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось оформить привоз' }; }
};

// POST /api/finance/stock/write-off/  body: { location_id?, comment?, items: [{ product_id, quantity }] }
// quantity можно дробным и с запятой: "1,8"
export const APIWriteOffStock = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/stock/write-off/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось списать материалы' }; }
};

// ════════════════════════════════════════════════════════════════════
//  СКИДКИ
// ════════════════════════════════════════════════════════════════════

export const APIGetDiscounts = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/discounts/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) { return handleError(error, []); }
};

export const APICreateDiscount = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/discounts/create/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось создать скидку' }; }
};

export const APIUpdateDiscount = async (token, id, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/api/finance/discounts/${id}/update/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось обновить скидку' }; }
};

export const APIDeleteDiscount = async (token, id) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/finance/discounts/${id}/delete/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось удалить скидку' }; }
};


// ════════════════════════════════════════════════════════════════════
//  КАТЕГОРИИ ТОВАРОВ / УСЛУГ
// ════════════════════════════════════════════════════════════════════

// GET /api/finance/categories/?is_active=true&with_counts=true
export const APIGetProductCategories = async (token, params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/finance/categories/`, {
            ...getAuthConfig(token), params,
        });
        return response.data;
    } catch (error) { return handleError(error, []); }
};

export const APICreateProductCategory = async (token, data) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/categories/create/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось создать категорию' }; }
};

export const APIUpdateProductCategory = async (token, id, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/api/finance/categories/${id}/update/`,
            data, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось обновить категорию' }; }
};

export const APIDeleteProductCategory = async (token, id) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/finance/categories/${id}/delete/`,
            getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось удалить категорию' }; }
};

// POST /api/finance/categories/<id>/add-products/  body: { product_ids: [..] }
export const APIAddProductsToCategory = async (token, categoryId, productIds) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/categories/${categoryId}/add-products/`,
            { product_ids: productIds }, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось добавить товары в категорию' }; }
};

// POST /api/finance/categories/<id>/remove-products/  body: { product_ids: [..] }
export const APIRemoveProductsFromCategory = async (token, categoryId, productIds) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/categories/${categoryId}/remove-products/`,
            { product_ids: productIds }, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось убрать товары из категории' }; }
};

// POST /api/finance/products/<id>/set-category/  body: { category_id: int|null }
export const APISetProductCategory = async (token, productId, categoryId) => {
    try {
        const response = await axios.post(
            `${API_URL}/api/finance/products/${productId}/set-category/`,
            { category_id: categoryId }, getAuthConfig(token),
        );
        return response.data;
    } catch (error) { return { error: error?.response?.data || 'Не удалось обновить категорию товара' }; }
};